import { Box, Paper, Typography } from '@mui/material';
import { FacilityZoneYardChecks, YardSpotRtlsCheck } from '../../api/yardCheck';
import { FRESHNESS_BUCKETS, NEEDS_REVIEW_BG, NEEDS_REVIEW_COLOR, formatAge, freshnessBucketFor } from './freshness';
import { formatYardSpotCardDetailLine } from './utils';

export interface YardSpotSelection {
  zone: FacilityZoneYardChecks;
  spot: YardSpotRtlsCheck;
}

interface YardChecksSpotGridProps {
  facilityZones: FacilityZoneYardChecks[];
  now: Date;
  onSelectSpot: (selection: YardSpotSelection) => void;
}

const YardChecksSpotGrid = ({ facilityZones, now, onSelectSpot }: YardChecksSpotGridProps) => {
  return (
    <Paper>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          flexWrap: 'wrap',
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">Every yard spot</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {FRESHNESS_BUCKETS.map(bucket => (
            <Box key={bucket.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 16, height: 8, flexShrink: 0, borderRadius: 0.5, bgcolor: bucket.color }} />
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {bucket.label}
              </Typography>
            </Box>
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 16, height: 8, flexShrink: 0, borderRadius: 0.5, bgcolor: NEEDS_REVIEW_COLOR }} />
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              Needs review
            </Typography>
          </Box>
        </Box>
      </Box>

      {facilityZones.map(zone => {
        const seenCount = zone.yardSpots.filter(
          spot => freshnessBucketFor(spot.latestHistory?.timestamp, now).key !== 'notSeen'
        ).length;

        return (
          <Box
            key={zone.facilityZoneUuid ?? 'unassigned'}
            sx={{ px: 3, py: 2.5, borderBottom: 1, borderColor: 'divider' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 15, fontWeight: 500, color: 'primary.main' }}>
                {zone.facilityZoneName || 'Unassigned'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {seenCount}/{zone.yardSpots.length} seen &middot; {zone.yardSpots.length - seenCount} overdue
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))',
                gap: 1,
                mt: 1.75,
              }}
            >
              {zone.yardSpots.map(spot => {
                const bucket = freshnessBucketFor(spot.latestHistory?.timestamp, now);
                const needsReview = Boolean(spot.latestHistory?.needsReview);
                return (
                  <Box
                    key={spot.yardSpotUuid}
                    onClick={() => onSelectSpot({ zone, spot })}
                    title={`${spot.yardSpotName} — last good read ${formatAge(
                      spot.latestHistory?.timestamp,
                      now
                    )} ago${needsReview ? ' — needs review' : ''}`}
                    sx={{
                      borderRadius: 1,
                      border: 1,
                      borderColor: needsReview ? NEEDS_REVIEW_COLOR : 'divider',
                      bgcolor: needsReview ? NEEDS_REVIEW_BG : bucket.bg,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 2, borderColor: needsReview ? NEEDS_REVIEW_COLOR : 'text.disabled' },
                    }}
                  >
                    <Box sx={{ height: 4, bgcolor: needsReview ? NEEDS_REVIEW_COLOR : bucket.color }} />
                    <Box sx={{ px: 1.125, pt: 0.875, pb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 500 }}>
                          {spot.yardSpotName}
                        </Typography>
                        <Typography
                          sx={{
                            ml: 'auto',
                            fontFamily: 'monospace',
                            fontSize: 11,
                            fontWeight: 500,
                            color: needsReview
                              ? NEEDS_REVIEW_COLOR
                              : bucket.key === 'fresh'
                              ? 'text.secondary'
                              : bucket.color,
                          }}
                        >
                          {formatAge(spot.latestHistory?.timestamp, now)}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          mt: 0.625,
                          fontSize: 12,
                          lineHeight: 1.35,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {formatYardSpotCardDetailLine(spot.latestHistory)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
};

export default YardChecksSpotGrid;
